"use client";

import { Activity, ArrowUpRight, FlaskConical, Leaf, Moon, MoveDiagonal, Wind } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DirectAnswerBlock() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const features = [{
    icon: Activity,
    category: "PRECISION FABRICATION",
    title: "Engineered to exact industrial tolerances.",
    body: "Custom-machined components built to your precise specifications, ensuring seamless integration into your existing infrastructure.",
    stat: "0.01mm",
    statContext: "TOLERANCE PRECISION"
  }, {
    icon: Leaf,
    category: "MATERIAL INTEGRITY",
    title: "Durability meets environmental standards.",
    body: "High-grade materials selected for longevity, chemical resistance, and compliance with rigorous industry safety protocols.",
    stat: "20 yrs",
    statContext: "MATERIAL LIFESPAN"
  }, {
    icon: Moon,
    category: "RAPID PROTOTYPING",
    title: "Accelerating your production cycle.",
    body: "Streamlined design-to-production workflows that minimize downtime and bring your concepts to market with unmatched speed.",
    stat: "48 hrs",
    statContext: "PROTOTYPE TURNAROUND"
  }, {
    icon: MoveDiagonal,
    category: "SCALABLE SOLUTIONS",
    title: "Systems that grow with your output.",
    body: "Modular fabrication strategies designed to scale alongside your operational demands without compromising quality or precision.",
    stat: "10k+",
    statContext: "UNITS PRODUCED"
  }, {
    icon: Wind,
    category: "SURFACE FINISHING",
    title: "Refining the final industrial touch.",
    body: "Advanced coating and finishing techniques that provide superior protection against corrosion, wear, and environmental stress.",
    stat: "99.9%",
    statContext: "FINISH CONSISTENCY"
  }, {
    icon: FlaskConical,
    category: "QUALITY ASSURANCE",
    title: "A continuous portrait of excellence.",
    body: "Rigorous multi-stage inspection protocols and longitudinal tracking so every component meets our uncompromising standards.",
    stat: "100 pts",
    statContext: "INSPECTION CHECKLIST"
  }];
  return <section data-section-id="3387" id="direct-answer-block" className="bg-background text-foreground py-24 md:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Industrial Fabrication &mdash; Tailored Solutions
          </p>
          <h2 className="font-default mt-6 text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight text-foreground">
            No two projects require the same <span className="italic">approach</span>.
          </h2>
          <p className="mt-8 mx-auto max-w-[640px] text-base md:text-lg leading-relaxed text-muted-foreground">
            Every fabrication process at our facility is managed individually &mdash; shaped by your technical requirements, your project history, and the specific demands of your industry. We do not believe in one-size-fits-all manufacturing; we believe in the singular precision of your vision.
          </p>
        </div>

        <div className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          const offset = i % 2 === 1 ? "lg:mt-6" : "";
          return <FeatureCard key={i} data-index={i} index={i} icon={Icon} category={feature.category} title={feature.title} body={feature.body} stat={feature.stat} statContext={feature.statContext} offsetClass={offset} />;
        })}
        </div>
      </div>
    </section>;
}
type FeatureCardProps = {
  index: number;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  category: string;
  title: string;
  body: string;
  stat: string;
  statContext: string;
  offsetClass: string;
  "data-index": number;
};
function FeatureCard({
  index,
  icon: Icon,
  category,
  title,
  body,
  stat,
  statContext,
  offsetClass,
  ...rest
}: FeatureCardProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={rest["data-index"]} style={{
    transitionDelay: `${index * 80}ms`
  }} className={`group relative rounded-lg border border-border bg-card p-8 md:p-10 transition-all duration-700 ease-out hover:-translate-y-1 hover:shadow-lg ${offsetClass} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="flex flex-col h-full">
        <div className="mb-8 transition-transform duration-500 ease-out group-hover:scale-105 group-hover:rotate-[-2deg]">
          <Icon className="size-12 text-primary" strokeWidth={1} />
        </div>

        <p className="font-default text-[0.65rem] uppercase tracking-[0.18em] text-muted-foreground">
          {category}
        </p>

        <h3 className="font-default mt-4 text-2xl md:text-[1.6rem] leading-[1.2] tracking-tight text-card-foreground">
          {title}
        </h3>

        <p className="mt-5 text-[0.95rem] leading-relaxed text-muted-foreground">
          {body}
        </p>

        <div className="mt-8 pt-6 border-t border-border flex items-end justify-between gap-4">
          <div>
            <p className="font-default italic text-3xl md:text-[2rem] leading-none text-primary">
              {stat}
            </p>
            <p className="font-default mt-2 text-[0.6rem] uppercase tracking-[0.18em] text-muted-foreground">
              {statContext}
            </p>
          </div>

          <span className="font-default text-[0.7rem] uppercase tracking-[0.15em] text-primary opacity-0 -translate-x-2 transition-all duration-500 ease-out group-hover:opacity-100 group-hover:translate-x-0 inline-flex items-center gap-1">
            Learn more
            <ArrowUpRight className="size-3.5" strokeWidth={1.5} />
          </span>
        </div>
      </div>
    </div>;
}